import { useEffect, useState } from 'preact/hooks';
import { api } from './api.ts';
import { toast } from './toast.ts';
import { changed } from './signal.ts';
import { DEFAULT_SETTINGS, type Me, type Settings } from '../shared/types.ts';
import { Today } from './views/Today.tsx';
import { Trends } from './views/Trends.tsx';
import { SettingsView } from './views/Settings.tsx';

type Route = 'today' | 'trends' | 'settings';

function route(): { name: Route; params: URLSearchParams } {
  const [name, query] = location.hash.replace(/^#/, '').split('?');
  return {
    name: name === 'trends' || name === 'settings' ? name : 'today',
    params: new URLSearchParams(query ?? ''),
  };
}

export function App() {
  const [r, setR] = useState(route);
  const [me, setMe] = useState<Me | null>(null);
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const tick = changed.use();

  useEffect(() => {
    const on = () => setR(route());
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);

  // A 401 here sends the browser to sign in (see api.ts).
  useEffect(() => {
    api<Me>('/api/me').then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    if (me) api<Settings>('/api/settings').then(setSettings).catch(() => {});
  }, [me, tick]);

  if (!me) return <div class="splash" />;

  return (
    <div class="shell">
      <main>
        {r.name === 'today' && <Today settings={settings} ai={me.ai} />}
        {r.name === 'trends' && <Trends settings={settings} />}
        {r.name === 'settings' && <SettingsView settings={settings} user={me.user} params={r.params} onSaved={setSettings} />}
      </main>
      <nav class="tabs">
        <a href="#today" class={r.name === 'today' ? 'on' : ''}>Diary</a>
        <a href="#trends" class={r.name === 'trends' ? 'on' : ''}>Trends</a>
        <a href="#settings" class={r.name === 'settings' ? 'on' : ''}>Settings</a>
      </nav>
      <ToastView />
    </div>
  );
}

function ToastView() {
  const t = toast.use();
  if (!t) return null;
  return (
    <div class={`toast ${t.error ? 'error' : ''}`} role="status">
      <span>{t.text}</span>
      {t.action && (
        <button
          onClick={() => {
            t.action!.run();
            toast.set(null);
          }}
        >
          {t.action.label}
        </button>
      )}
    </div>
  );
}
